import React, { FC, useCallback, useEffect } from 'react';
import { observer } from 'mobx-react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  DialogActions,
  DialogContent,
  Divider,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Controller, useForm } from 'react-hook-form';
import api from 'Api/ApiMethods';
import Input from 'components/UI/Input/Input';
import { Select } from 'components/UI/Select/Select';
import { MultiSelect } from 'components/UI/MultiSelect/MultiSelect';
import Dialog from 'components/UI/Dialog';
import Text from 'components/UI/Text';
import Button from 'components/UI/Button';
import { theme } from 'components/UI';
import Loading from 'components/UI/Loading/Loading';
import Grid from 'components/UI/Grid';
import { useStores } from 'stores/hooks/hooks';
import { ActionMode, DialogOnCloseReason } from 'models/shared';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

interface PlotGBIDialogProps {
  onHide: () => void;
  visible: boolean;
  title: string;
  mode: ActionMode;
}

interface SchemaProperty {
  type?: string;
  title?: string;
  description?: string;
  enum?: unknown[];
  default?: unknown;
  minimum?: number;
  maximum?: number;
  pattern?: string;
  items?: { enum?: unknown[]; type?: string };
  uniqueItems?: boolean;
  $ref?: string;
}

interface SchemaDefinition {
  enum?: unknown[];
  options?: { enum_titles?: string[] };
  items?: { enum?: unknown[]; type?: string };
}

interface IrrigationSchema {
  properties: Record<string, SchemaProperty>;
  definitions: Record<string, SchemaDefinition>;
  required: string[];
  advanced: string[];
}

interface SelectOption {
  label: string;
  value: unknown;
}

type FormValues = Record<string, unknown>;

const StyledDialogContent = styled(DialogContent)`
  padding: 0.5rem 1.5rem;
  min-height: 300px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FieldRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
`;

const FieldLabel = styled(Typography)`
  min-width: 220px;
  font-size: 0.85rem;
  color: var(--text-color);
`;

const StyledAccordion = styled(Accordion)`
  box-shadow: none;
  border: 1px solid var(--surface-border);
  border-radius: 4px !important;
  background-color: transparent;
  &::before {
    display: none;
  }
  &.Mui-expanded {
    margin: 0;
  }
`;

const StyledAccordionSummary = styled(AccordionSummary)`
  min-height: 40px;
  padding: 0 12px;
  &.Mui-expanded {
    min-height: 40px;
  }
  .MuiAccordionSummary-content {
    margin: 8px 0;
  }
`;

const StyledAccordionDetails = styled(AccordionDetails)`
  padding: 8px 8px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

function resolveOptions(
  prop: SchemaProperty,
  definitions: Record<string, SchemaDefinition>,
): SelectOption[] | null {
  if (prop.$ref) {
    const defName = prop.$ref.replace('#/definitions/', '');
    const def = definitions[defName];
    if (!def) return null;

    if (def.items?.enum) {
      return def.items.enum.map((v) => ({ value: v, label: String(v) }));
    }

    const enums = def.enum ?? [];
    const titles = def.options?.enum_titles ?? [];
    return enums.map((v, i) => ({ value: v, label: titles[i] ?? String(v) }));
  }

  if (prop.enum) {
    return prop.enum.map((v) => ({ value: v, label: String(v) }));
  }

  return null;
}

const PlotGBIDialog: FC<PlotGBIDialogProps> = observer(
  ({ visible = false, onHide, title }) => {
    const { t: generalT } = useTranslation('general')
    const { resellersStore, rootStore } = useStores();
    const plotId = resellersStore.selectedRowsPlots?.[0]?.id;

    const [schema, setSchema] = React.useState<IrrigationSchema | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [advancedExpanded, setAdvancedExpanded] = React.useState(false);

    const {
      control,
      handleSubmit,
      reset,
      formState: { errors },
    } = useForm<FormValues>({ mode: 'onChange' });

    useEffect(() => {
      if (!visible || !plotId) return undefined;

      const controller = new AbortController();
      setLoading(true);

      api
        .getPlotGbiParams(plotId, controller)
        .then((res) => {
          if (!res) return;
          setSchema(res.schema);

          const properties: Record<string, SchemaProperty> =
            res.schema?.properties ?? {};

          const params: Record<string, unknown> = res.params ?? {};

          const defaults = Object.fromEntries(
            Object.entries(properties).map(([key, prop]) => [
              key,
              params[key] ?? prop.default ?? '',
            ]),
          );

          reset({ ...defaults, comment: '' });
        })
        .finally(() => setLoading(false));

      return () => controller.abort();
    }, [visible, plotId, reset]);

    const onError = useCallback(
      (formErrors: Record<string, unknown>) => {
        const firstKey = Object.keys(formErrors)[0];
        if (!firstKey) return;

        if (schema?.advanced.includes(firstKey)) {
          setAdvancedExpanded(true);
        }

        setTimeout(() => {
          document
            .getElementById(firstKey)
            ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 50);
      },
      [schema],
    );

    const handleClose = useCallback(
      (_event: unknown, reason: DialogOnCloseReason) => {
        if (reason !== DialogOnCloseReason.BackDropClick) onHide();
      },
      [onHide],
    );

    const onSubmit = useCallback(
      async (values: FormValues) => {
        const { comment, ...allParams } = values;
        // We delete non required keys, to keep behavior same as old web
        const requiredKeys = new Set(schema?.required ?? []);
        const params = Object.fromEntries(
          Object.entries(allParams).filter(([key]) => requiredKeys.has(key)),
        );

        try {
          setSaving(true);
          const res = await api.postPlotGbiParams(
            plotId,
            params,
            comment as string,
          );

          if (res) onHide();
        } catch {
          rootStore?.snackBarStore.showError('Error saving GBI params');
        } finally {
          setSaving(false);
        }
      },
      [plotId, onHide, rootStore?.snackBarStore, schema?.required],
    );

    const renderField = useCallback(
      (key: string) => {
        if (!schema) return null;
        const prop = schema.properties[key];
        if (!prop) return null;

        const label = prop.title || key;
        const description = prop.description ?? '';
        const error = errors[key];

        if (prop.type === 'array') {
          const options = resolveOptions(prop, schema.definitions);
          return (
            <FieldRow key={key} id={key}>
              <Tooltip title={description} placement="top">
                <FieldLabel>{label}</FieldLabel>
              </Tooltip>
              <Controller
                name={key}
                control={control}
                render={({ field: { value, onChange } }) => (
                  <MultiSelect
                    options={options ?? []}
                    optionLabel="label"
                    optionValue="value"
                    value={value as unknown[]}
                    onChange={(e) => onChange(e.value)}
                    style={{ flex: 1 }}
                  />
                )}
              />
            </FieldRow>
          );
        }

        const options = resolveOptions(prop, schema.definitions);
        if (options) {
          return (
            <FieldRow key={key} id={key}>
              <Tooltip title={description} placement="top">
                <FieldLabel>{label}</FieldLabel>
              </Tooltip>
              <Controller
                name={key}
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Select
                    options={options}
                    optionLabel="label"
                    optionValue="value"
                    value={value}
                    onChange={(e) => onChange(e.value)}
                    error={!!error}
                    style={{ flex: 1 }}
                  />
                )}
              />
            </FieldRow>
          );
        }

        const isNumeric = prop.type === 'number' || prop.type === 'integer';
        return (
          <FieldRow key={key} id={key}>
            <Tooltip title={description} placement="top">
              <FieldLabel>{label}</FieldLabel>
            </Tooltip>
            <Controller
              name={key}
              control={control}
              rules={{
                required: schema.required.includes(key)
                  ? `${label} is required`
                  : false,
                ...(isNumeric && {
                  valueAsNumber: true,
                  min:
                    prop.minimum !== undefined
                      ? { value: prop.minimum, message: `Min: ${prop.minimum}` }
                      : undefined,
                  max:
                    prop.maximum !== undefined
                      ? { value: prop.maximum, message: `Max: ${prop.maximum}` }
                      : undefined,
                }),
              }}
              render={({ field }) => (
                <Input
                  {...field}
                  value={field.value as string | number}
                  type={isNumeric ? 'number' : 'text'}
                  size="small"
                  error={!!error}
                  helperText={error?.message as string}
                  onChange={(e) =>
                    isNumeric
                      ? field.onChange(
                        (e.target as HTMLInputElement).valueAsNumber,
                      )
                      : field.onChange(e.target.value)
                  }
                  style={{ flex: 1 }}
                />
              )}
            />
          </FieldRow>
        );
      },
      [schema, errors, control],
    );

    const advancedKeys = schema?.advanced ?? [];

    const fieldKeys = schema
      ? Object.keys(schema.properties).filter(
        (key) => schema.required.includes(key) && !advancedKeys.includes(key),
      )
      : [];

    return (
      <Dialog
        open={visible}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        dialogTitle={title}
        titleWeight="bold"
        disableEscapeKeyDown
      >
        <StyledDialogContent>
          {loading ? (
            <Grid
              direction="row"
              align="center"
              justify="center"
              style={{ height: '300px' }}
            >
              <Loading />
            </Grid>
          ) : (
            <>
              {fieldKeys.map(renderField)}

              {advancedKeys.length > 0 && (
                <StyledAccordion
                  expanded={advancedExpanded}
                  onChange={(_, expanded) => setAdvancedExpanded(expanded)}
                >
                  <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography fontWeight={600} fontSize="0.9rem">
                      Advanced
                    </Typography>
                  </StyledAccordionSummary>
                  <StyledAccordionDetails>
                    {advancedKeys.map(renderField)}
                  </StyledAccordionDetails>
                </StyledAccordion>
              )}

              <FieldRow id="comment" style={{ marginTop: '8px' }}>
                <Controller
                  name="comment"
                  control={control}
                  rules={{ required: 'Comment is required' }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      value={field.value as string}
                      label="Comment"
                      multiline
                      rows={2}
                      fullWidth
                      error={!!errors.comment}
                      helperText={errors.comment?.message as string}
                    />
                  )}
                />
              </FieldRow>
            </>
          )}
        </StyledDialogContent>
        <Divider />
        <DialogActions>
          <Button
            onClick={handleSubmit(onSubmit, onError)}
            loading={saving}
            disabled={loading || saving}
          >
            <Text textcolor={theme.color.white} size="sm">
              {generalT('save')}
            </Text>
          </Button>
          <Button onClick={onHide}>
            <Text textcolor={theme.color.white} size="sm">
              {generalT('cancel')}
            </Text>
          </Button>
        </DialogActions>
      </Dialog>
    );
  },
);

export default PlotGBIDialog;
